import { Link } from 'react-router-dom';
import { ArrowLeft, CalendarClock, Folder, Pencil, Plus, Sparkles, Swords, Users } from 'lucide-react';
import { Button, Card, Tag } from '@/components/ui';
import { UpcomingDeadlines } from '@/components/UpcomingDeadlines';
import { useMatters } from '@/contexts/MatterContext';

export function Matters() {
  const { matters, loading, error } = useMatters();

  return (
    <main className="min-h-screen bg-stone-950 px-6 py-10">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <Link
            to="/hearing"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to hearing
          </Link>
          <Link to="/matters/new">
            <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
              New matter
            </Button>
          </Link>
        </div>

        <header className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500">Matters</p>
          <h1 className="font-display text-3xl italic text-amber-500">Active matters</h1>
          <p className="text-sm text-stone-400">
            Each matter holds the case context, folder paths, and email keywords Claude uses to
            ground its responses. Edit any matter to update what the model sees.
          </p>
        </header>

        <UpcomingDeadlines />

        {error ? (
          <Card variant="default" padding="md" className="border-red-900/40 bg-red-950/10">
            <p className="text-sm text-red-300">Could not load matters: {error}</p>
          </Card>
        ) : null}

        {loading && matters.length === 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <Card key={i} variant="panel" padding="md" className="space-y-3">
                <div className="h-5 w-2/3 animate-pulse rounded bg-stone-800" />
                <div className="h-3 w-1/3 animate-pulse rounded bg-stone-800/70" />
                <div className="h-3 w-full animate-pulse rounded bg-stone-800/50" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-stone-800/50" />
              </Card>
            ))}
          </div>
        ) : matters.length === 0 ? (
          <Card variant="panel" padding="lg" className="text-center">
            <Folder className="mx-auto mb-3 h-8 w-8 text-amber-500" />
            <p className="font-display text-xl italic text-stone-100">Create your first matter</p>
            <p className="mt-1 text-sm text-stone-400">
              Start from a template, or a blank matter. A matter holds the case context, folders, and
              documents the AI uses to ground its answers.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {[
                { key: 'court', label: 'Court matter' },
                { key: 'afca', label: 'AFCA' },
                { key: 'family', label: 'Family' },
                { key: 'property', label: 'Property' },
              ].map((t) => (
                <Link key={t.key} to={`/matters/new?template=${t.key}`}>
                  <Button variant="ghost" size="sm">
                    {t.label}
                  </Button>
                </Link>
              ))}
              <Link to="/matters/new">
                <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                  Blank matter
                </Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {matters.map((matter) => (
              <Card
                key={matter.id}
                variant="panel"
                padding="md"
                className="flex h-full flex-col transition-colors hover:border-stone-700"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-display text-lg italic text-stone-100">{matter.name}</p>
                  {matter.archived ? <Tag tone="muted">Archived</Tag> : null}
                </div>
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  {[matter.reference, matter.court].filter(Boolean).join(' · ') || 'No reference'}
                </p>
                <p className="mt-3 line-clamp-3 flex-1 text-sm text-stone-400">
                  {matter.context || 'No context yet. Open to add.'}
                </p>
                <div className="mt-3 flex items-center gap-3 border-t border-stone-800/70 pt-3">
                  <Link
                    to={`/matters/${matter.id}`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-stone-400 hover:text-amber-400"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Open
                  </Link>
                  <Link
                    to={`/matters/${matter.id}/prep`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-amber-500 hover:text-amber-400"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Prep
                  </Link>
                  <Link
                    to={`/matters/${matter.id}/witnesses`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-amber-500 hover:text-amber-400"
                  >
                    <Users className="h-3.5 w-3.5" /> Witnesses
                  </Link>
                  <Link
                    to={`/matters/${matter.id}/battle-map`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-amber-500 hover:text-amber-400"
                  >
                    <Swords className="h-3.5 w-3.5" /> Battle map
                  </Link>
                  <Link
                    to={`/matters/${matter.id}/deadlines`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-amber-500 hover:text-amber-400"
                  >
                    <CalendarClock className="h-3.5 w-3.5" /> Deadlines
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
